import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '@noor/lib/cn';

export type SwitchTone = 'brand' | 'neutral';

function Switch({
  className,
  tone = 'brand',
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root> & {
  /**
   * Checked-track treatment. `brand` (default) fills brand orange; `neutral`
   * fills warm gray — the Figma toggle card's second row.
   */
  tone?: SwitchTone;
}) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      data-tone={tone}
      className={cn(
        // Figma toggle: 44×24 track, 20px white thumb riding 2px insets with
        // the two-layer thumb lift. Off is #e4e7ec; on is brand orange (or
        // warm gray for `neutral`). Disabled parks on #f2f4f7 / #f9fafb.
        // Metrics resolve through `--switch-*` sizing handles.
        'group peer inline-flex h-[var(--switch-height)] w-[var(--switch-width)] shrink-0 items-center rounded-[var(--switch-radius)] transition-all outline-none',
        'focus-visible:border-control-ring focus-visible:ring-control-ring/50 focus-visible:ring-[3px]',
        'data-[state=unchecked]:bg-control-bg',
        tone === 'brand'
          ? 'data-[state=checked]:bg-control-bg-checked'
          : 'data-[state=checked]:bg-switch-track-bg-checked-neutral',
        'disabled:cursor-not-allowed disabled:bg-switch-track-bg-disabled',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block h-[var(--switch-thumb-size)] w-[var(--switch-thumb-size)] rounded-[var(--switch-thumb-radius)] bg-switch-thumb-bg shadow-switch-thumb ring-0 transition-transform',
          // Checked offset is derived (track − thumb − inset) so resizing
          // either end keeps the thumb parked exactly on the far edge.
          'data-[state=checked]:translate-x-[calc(var(--switch-width)-var(--switch-thumb-size)-var(--switch-thumb-inset))] data-[state=unchecked]:translate-x-[var(--switch-thumb-inset)]',
          'group-data-[disabled]:bg-form-control-bg-disabled',
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
